import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { trackActivity, portfolioRegistryAPI } from '../services/api';
import PortfolioMixer from '../components/PortfolioMixer';
import AssetAllocationChart from '../components/AssetAllocationChart';

import BoxPlotChart from '../components/BoxPlotChart';
import StochasticChart from '../components/StochasticChart';
import ReturnsHistogramChart from '../components/ReturnsHistogramChart';
import StressTest from '../components/StressTest';
import BlenderMethodologyModal from '../components/BlenderMethodologyModal';
import { buildDisplayNameMap } from '../utils/portfolioNames';
import ManagedDisclaimer from '../components/ManagedDisclaimer';


const PortfolioBlenderApp = () => {
  const [availablePortfolios, setAvailablePortfolios] = useState([]);
  const [portfolioRegistry, setPortfolioRegistry] = useState(null);
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [lastWeights, setLastWeights] = useState(null);

  // Chart refs for PDF export
  const allocationRef = useRef(null);
  const boxPlotRef = useRef(null);
  const stochasticRef = useRef(null);
  const histogramRef = useRef(null);
  const stressTestRef = useRef(null);

  // Track app access and fetch available portfolios + registry on mount
  useEffect(() => {
    trackActivity('app_access', 'portfolio_blender');
    Promise.all([
      api.get('/api/blender/available-portfolios'),
      portfolioRegistryAPI.getRegistry('portfolio_viewer').catch(() => null),
    ])
      .then(([availableRes, registryRes]) => {
        setAvailablePortfolios(availableRes.data);
        if (registryRes) setPortfolioRegistry(registryRes);
      })
      .catch((err) => {
        console.error('Error fetching portfolios:', err);
        setError('Failed to load portfolios. Please check the backend is running.');
      });
  }, []);

  const captureChartImages = useCallback(() => {
    const images = {};
    const refMap = {
      allocation: allocationRef,
      box_plot: boxPlotRef,
      stochastic: stochasticRef,
      histogram: histogramRef,
      stress_test: stressTestRef,
    };
    for (const [key, ref] of Object.entries(refMap)) {
      try {
        if (ref.current && ref.current.toBase64Image) {
          images[key] = ref.current.toBase64Image();
        }
      } catch (e) {
        console.warn(`Failed to capture ${key}:`, e);
      }
    }
    return images;
  }, []);

  const handleExportPdf = useCallback(async () => {
    if (!results) return;
    setIsPdfLoading(true);
    try {
      const chartImages = captureChartImages();

      // Build portfolio label from weights (covers blender codes AND sub-asset
      // class codes, resolving each to its admin-set friendly name).
      const nameMap = buildDisplayNameMap(portfolioRegistry);
      const label = lastWeights
        ? Object.entries(lastWeights)
            .filter(([, w]) => w > 0)
            .map(([code, w]) => `${nameMap[code] || code} ${Math.round(w * 100)}%`)
            .join(' / ')
        : 'Custom Blend';

      const response = await api.post(
        '/api/blender/export-pdf',
        {
          results: results,
          chart_images: chartImages,
          portfolio_label: label,
          portfolio_display_names: nameMap,
        },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'portfolio_blend_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export error:', err);
      setError('PDF export failed. Please try again.');
    } finally {
      setIsPdfLoading(false);
    }
  }, [results, captureChartImages, lastWeights, portfolioRegistry]);

  const handleCalculate = (weights) => {
    setIsLoading(true);
    setError(null);
    setLastWeights(weights);

    api
      .post('/api/blender/calculate-blend', { weights })
      .then((response) => {
        if (response.data.error) {
          setError(response.data.error);
        } else {
          setResults(response.data);
        }
      })
      .catch((err) => {
        console.error('Calculation error:', err);
        setError('Calculation failed. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="container-fluid px-4 mt-4 fade-in">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Dashboard Header */}
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-start">
          <div>
            <h2 className="mb-1">Portfolio Blender</h2>
            <p className="text-muted">Portfolio Analysis & Stochastic Projections</p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setShowMethodology(true)}
            >
              <i className="bi bi-journal-text me-1"></i>Methodology
            </button>
            {results && (
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleExportPdf}
                disabled={isPdfLoading}
              >
                {isPdfLoading ? (
                  <><span className="spinner-border spinner-border-sm me-1"></span>Exporting...</>
                ) : (
                  <><i className="bi bi-file-earmark-pdf me-1"></i>Export PDF</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Portfolio Composition */}
      <div className="row mb-4">
        <div className="col-lg-6">
          <PortfolioMixer
            availablePortfolios={availablePortfolios}
            portfolioRegistry={portfolioRegistry}
            onCalculate={handleCalculate}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Row 1: Lookthrough Table (left) + Pie Chart (right) */}
      {results && results.lookthrough && (
        <div className="row mb-4">
          <div className="col-lg-7">
            <div className="card h-100">
              <div className="card-header">
                <h5>Asset Class Lookthrough</h5>
              </div>
              <div className="card-body p-0">
                {(() => {
                  const lookthrough = results.lookthrough || {};
                  const perPortfolio = results.per_portfolio_lookthrough || {};
                  const perSummary = results.per_portfolio_summary || {};
                  const portfolioNames = Object.keys(perPortfolio);
                  const hasCombined = portfolioNames.length > 1;
                  const cellStyle = { padding: '6px 12px' };
                  const labelStyle = { ...cellStyle, fontWeight: 500 };
                  const combinedBorder = { borderLeft: '2px solid rgba(0,0,0,0.1)' };
                  const sectionStyle = { backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)', fontWeight: 700, padding: '8px 12px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' };

                  // blender_code AND sub-asset-class → friendly name lookup.
                  const nameMap = buildDisplayNameMap(portfolioRegistry);
                  const displayName = (code) => nameMap[code] || code;

                  // Growth broad classes come from the live taxonomy
                  // (BroadAssetClass.is_growth) via the registry endpoint, so
                  // adding a new growth category in investment-config flows
                  // through without code edits.
                  const growthAcs = portfolioRegistry?.growth_broad_classes || [];
                  const combinedGrowth = Math.round(growthAcs.reduce((s, ac) => s + (lookthrough[ac] || 0), 0) * 100);
                  const combinedDefence = 100 - combinedGrowth;

                  const renderRow = (label, getValue, getCombined, opts = {}) => (
                    <tr key={label} style={opts.style}>
                      <td style={{ ...labelStyle, ...(opts.bold ? { fontWeight: 600 } : {}) }}>{label}</td>
                      {portfolioNames.map(name => (
                        <td key={name} className="text-end" style={{ ...cellStyle, ...(opts.bold ? { fontWeight: 600 } : {}) }}>
                          {getValue(name)}
                        </td>
                      ))}
                      {hasCombined && (
                        <td className="text-end" style={{ ...cellStyle, ...combinedBorder, fontWeight: 600 }}>
                          {getCombined()}
                        </td>
                      )}
                    </tr>
                  );

                  return (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
                        <th style={{ padding: '8px 12px', fontWeight: 600 }}>Asset Class</th>
                        {portfolioNames.map(name => (
                          <th key={name} className="text-end" style={{ padding: '8px 12px', fontWeight: 600 }}>{displayName(name)}</th>
                        ))}
                        {hasCombined && (
                          <th className="text-end" style={{ padding: '8px 12px', fontWeight: 700, borderLeft: '2px solid rgba(255,255,255,0.3)' }}>Combined</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(lookthrough)
                        .filter(ac => (lookthrough[ac] || 0) > 0.001)
                        .map(ac => (
                          <tr key={ac}>
                            <td style={labelStyle}>{ac}</td>
                            {portfolioNames.map(name => (
                              <td key={name} className="text-end" style={cellStyle}>
                                {((perPortfolio[name]?.[ac] || 0) * 100).toFixed(1)}%
                              </td>
                            ))}
                            {hasCombined && (
                              <td className="text-end" style={{ ...cellStyle, ...combinedBorder, fontWeight: 600 }}>
                                {((lookthrough[ac] || 0) * 100).toFixed(1)}%
                              </td>
                            )}
                          </tr>
                        ))}
                      <tr style={{ borderTop: '2px solid var(--primary-color)', fontWeight: 700 }}>
                        <td style={{ padding: '8px 12px' }}>Total</td>
                        {portfolioNames.map(name => {
                          const total = Object.values(perPortfolio[name] || {}).reduce((s, v) => s + (v || 0), 0);
                          return <td key={name} className="text-end" style={{ padding: '8px 12px' }}>{(total * 100).toFixed(1)}%</td>;
                        })}
                        {hasCombined && (
                          <td className="text-end" style={{ padding: '8px 12px', ...combinedBorder }}>
                            {(Object.values(lookthrough).reduce((s, v) => s + (v || 0), 0) * 100).toFixed(1)}%
                          </td>
                        )}
                      </tr>

                      {/* Growth / Defence Summary */}
                      {renderRow('Growth Total',
                        name => perSummary[name]?.growth_total != null ? `${perSummary[name].growth_total}%` : '—',
                        () => `${combinedGrowth}%`,
                        { bold: true }
                      )}
                      {renderRow('Defence Total',
                        name => perSummary[name]?.defence_total != null ? `${perSummary[name].defence_total}%` : '—',
                        () => `${combinedDefence}%`,
                        { bold: true }
                      )}

                      {/* Risk Metrics Section */}
                      <tr>
                        <td colSpan={portfolioNames.length + (hasCombined ? 2 : 1)} style={sectionStyle}>Risk Metrics</td>
                      </tr>
                      {renderRow('Time Horizon',
                        name => perSummary[name]?.time_horizon ?? '—',
                        () => ''
                      )}
                      {renderRow('Expected Return',
                        name => perSummary[name]?.expected_return != null ? `${(perSummary[name].expected_return * 100).toFixed(1)}%` : '—',
                        () => results.return != null ? `${(results.return * 100).toFixed(1)}%` : '—'
                      )}
                      {renderRow('Expected Volatility',
                        name => perSummary[name]?.expected_volatility != null ? `${(perSummary[name].expected_volatility * 100).toFixed(1)}%` : '—',
                        () => results.volatility != null ? `${(results.volatility * 100).toFixed(1)}%` : '—'
                      )}
                      {renderRow('Est. Income Yield',
                        name => perSummary[name]?.expected_income != null ? `${(perSummary[name].expected_income * 100).toFixed(1)}%` : '—',
                        () => results.income != null ? `${(results.income * 100).toFixed(1)}%` : '—'
                      )}
                      {renderRow('Mgmt Fees & Costs',
                        name => perSummary[name]?.expected_icr != null ? `${perSummary[name].expected_icr.toFixed(2)}%` : '—',
                        () => results.icr != null ? `${results.icr.toFixed(2)}%` : '—'
                      )}
                      {renderRow('Est. Negative Years (20yr)',
                        name => perSummary[name]?.est_negative_years != null ? perSummary[name].est_negative_years.toFixed(1) : '—',
                        () => results.stochastics?.metrics?.expected_negative_years != null ? results.stochastics.metrics.expected_negative_years.toFixed(1) : '—'
                      )}
                      {renderRow('SRM Risk Band',
                        name => perSummary[name]?.srm_risk_band != null ? perSummary[name].srm_risk_band : '—',
                        () => '—'
                      )}
                      {renderRow('SRM Risk Label',
                        name => perSummary[name]?.srm_risk_label ?? '—',
                        () => '—'
                      )}

                      {/* Liquidity Profile */}
                      {results.liquidity_metrics && results.liquidity_metrics.length > 0 && (() => {
                        const liqByPortfolio = {};
                        const blended = results.liquidity_metrics.find(m => m.portfolio === 'Blended Portfolio');
                        results.liquidity_metrics.forEach(m => { if (m.portfolio !== 'Blended Portfolio') liqByPortfolio[m.portfolio] = m; });
                        const fmtPct = v => `${(v * 100).toFixed(0)}%`;
                        return (
                          <>
                            <tr>
                              <td colSpan={portfolioNames.length + (hasCombined ? 2 : 1)} style={sectionStyle}>Liquidity Profile</td>
                            </tr>
                            {renderRow('Daily Liquid',
                              name => liqByPortfolio[name] ? fmtPct(liqByPortfolio[name].daily) : '—',
                              () => blended ? fmtPct(blended.daily) : '—'
                            )}
                            {renderRow('Monthly Liquid',
                              name => liqByPortfolio[name] ? fmtPct(liqByPortfolio[name].monthly) : '—',
                              () => blended ? fmtPct(blended.monthly) : '—'
                            )}
                            {renderRow('Quarterly Liquid',
                              name => liqByPortfolio[name] ? fmtPct(liqByPortfolio[name].quarterly) : '—',
                              () => blended ? fmtPct(blended.quarterly) : '—'
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
                  );
                })()}
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card h-100">
              <div className="card-header">
                <h5>Asset Allocation</h5>
              </div>
              <div className="card-body" style={{ height: '500px' }}>
                <AssetAllocationChart ref={allocationRef} data={results.lookthrough} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stress Testing & Analytics Section */}
      {results && (
        <>
          {/* Row 2: Charts */}
          <div className="row mb-4">
            {results.box_plot_data && (
              <div className="col-lg-6 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5>Return Distribution</h5>
                  </div>
                  <div className="card-body" style={{ height: '500px' }}>
                    <BoxPlotChart ref={boxPlotRef} data={results.box_plot_data} portfolioRegistry={portfolioRegistry} />
                  </div>
                </div>
              </div>
            )}
            {results.stochastics && results.stochastics.paths && (
              <div className="col-lg-6 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5>30-Year Monte Carlo Projection</h5>
                  </div>
                  <div className="card-body" style={{ height: '500px' }}>
                    <StochasticChart ref={stochasticRef} paths={results.stochastics.paths} />
                  </div>
                </div>
              </div>
            )}
            {results.returns_histograms && (
              <div className="col-lg-6 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5>Annualised Returns Distribution</h5>
                  </div>
                  <div className="card-body" style={{ height: '500px' }}>
                    <ReturnsHistogramChart ref={histogramRef} histograms={results.returns_histograms} portfolioRegistry={portfolioRegistry} />
                  </div>
                </div>
              </div>
            )}
            {results.stress_test_results && (
              <div className="col-lg-6 mb-4">
                <StressTest
                  ref={stressTestRef}
                  stressTestResults={results.stress_test_results}
                  perPortfolioStress={results.per_portfolio_stress}
                  weights={lastWeights}
                  portfolioRegistry={portfolioRegistry}
                />
              </div>
            )}
            {results.stochastics && results.stochastics.metrics && (
              <div className="col-lg-6 mb-4">
                <div className="card h-100">
                  <div className="card-header">
                    <h5>Deep Analytics</h5>
                  </div>
                  <div className="card-body p-0">
                    {(() => {
                      const perRisk = results.per_portfolio_risk_metrics || {};
                      const combinedMetrics = results.stochastics.metrics;
                      const portfolioNames = Object.keys(perRisk);
                      const hasCombined = portfolioNames.length > 1;
                      const cellStyle = { padding: '6px 12px', fontSize: '0.85rem' };
                      const labelStyle = { ...cellStyle, fontWeight: 500 };
                      const combinedBorder = { borderLeft: '2px solid rgba(0,0,0,0.1)' };
                      const sectionStyle = { backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)', fontWeight: 700, padding: '8px 12px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' };

                      // blender_code AND sub-asset-class → friendly name lookup.
                      const nameMap = buildDisplayNameMap(portfolioRegistry);
                      const displayName = (code) => nameMap[code] || code;

                      const fmtPct1 = v => v != null ? (v * 100).toFixed(1) + '%' : '—';
                      const fmtPct2 = v => v != null ? (v * 100).toFixed(2) + '%' : '—';
                      const fmt2dp = v => v != null ? v.toFixed(2) : '—';
                      const fmt1dp = v => v != null ? v.toFixed(1) : '—';

                      const tooltips = {
                        'Return/Risk Ratio': 'Expected return divided by volatility. Higher values indicate better compensation per unit of risk.',
                        'Sortino Ratio': '(Return − risk-free rate) / downside deviation. Like Sharpe but only penalises downside volatility.',
                        'Calmar Ratio': 'Expected return / average max drawdown. Measures return relative to the worst peak-to-trough decline.',
                        'Avg Max Drawdown': 'Average of the largest peak-to-trough decline across all Monte Carlo simulations.',
                        'Worst Max Drawdown': 'The single largest peak-to-trough decline observed across all 10,000 simulations — a roughly 1-in-10,000 outcome and therefore extremely unlikely.',
                        'Worst 1-Year Return': 'The lowest single-year return observed across all simulated years.',
                        'VaR (95%)': 'Value at Risk: the 5th percentile of 1-year returns. 95% of outcomes are expected to be better than this.',
                        'CVaR (95%)': 'Conditional VaR: the average return in the worst 5% of 1-year outcomes. Measures expected loss in the tail.',
                        'Win Rate': 'Percentage of simulated years with a positive return.',
                        'Objective': 'The portfolio\'s return objective, sourced from the portfolio registry. Can be CPI+, Cash+, or an absolute target.',
                        'Prob. Meeting Objective': 'Probability that terminal wealth exceeds the return objective target over the portfolio\'s time horizon, based on Monte Carlo simulation.',
                        'Est. Negative Years (20yr)': 'Expected number of years with a negative return in a 20-year period.',
                      };

                      const renderRow = (label, getVal, getCombined) => (
                        <tr key={label}>
                          <td style={{ ...labelStyle, cursor: tooltips[label] ? 'help' : 'default' }} title={tooltips[label] || ''}>{label}</td>
                          {portfolioNames.map(name => (
                            <td key={name} className="text-end" style={cellStyle}>
                              {getVal(perRisk[name])}
                            </td>
                          ))}
                          {hasCombined && (
                            <td className="text-end" style={{ ...cellStyle, ...combinedBorder, fontWeight: 600 }}>
                              {getCombined()}
                            </td>
                          )}
                        </tr>
                      );

                      // If no per-portfolio data, fall back to combined-only single column
                      if (portfolioNames.length === 0) {
                        const m = combinedMetrics;
                        return (
                          <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                              <thead>
                                <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
                                  <th style={{ padding: '8px 12px' }}>Metric</th>
                                  <th className="text-end" style={{ padding: '8px 12px' }}>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr><td colSpan="2" style={sectionStyle}>Return & Risk</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Return/Risk Ratio']}>Return/Risk Ratio</td><td className="text-end" style={cellStyle}>{fmt2dp(m.return_risk_ratio)}</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Sortino Ratio']}>Sortino Ratio</td><td className="text-end" style={cellStyle}>{fmt2dp(m.sortino_ratio)}</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Calmar Ratio']}>Calmar Ratio</td><td className="text-end" style={cellStyle}>{fmt2dp(m.calmar_ratio)}</td></tr>
                                <tr><td colSpan="2" style={sectionStyle}>Drawdown</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Avg Max Drawdown']}>Avg Max Drawdown</td><td className="text-end" style={cellStyle}>{fmtPct1(m.avg_max_drawdown)}</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Worst Max Drawdown']}>Worst Max Drawdown</td><td className="text-end" style={cellStyle}>{fmtPct1(m.worst_max_drawdown)}</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Worst 1-Year Return']}>Worst 1-Year Return</td><td className="text-end" style={cellStyle}>{fmtPct1(m.worst_1yr)}</td></tr>
                                <tr><td colSpan="2" style={sectionStyle}>Tail Risk</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['VaR (95%)']}>VaR (95%)</td><td className="text-end" style={cellStyle}>{fmtPct1(m.var_95)}</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['CVaR (95%)']}>CVaR (95%)</td><td className="text-end" style={cellStyle}>{fmtPct1(m.cvar_95)}</td></tr>
                                <tr><td colSpan="2" style={sectionStyle}>Probability</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Win Rate']}>Win Rate</td><td className="text-end" style={cellStyle}>{fmtPct1(m.win_rate)}</td></tr>
                                <tr><td style={{ ...labelStyle, cursor: 'help' }} title={tooltips['Est. Negative Years (20yr)']}>Est. Negative Years (20yr)</td><td className="text-end" style={cellStyle}>{fmt1dp(m.expected_negative_years)}</td></tr>
                              </tbody>
                            </table>
                          </div>
                        );
                      }

                      const colCount = portfolioNames.length + (hasCombined ? 2 : 1);

                      return (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
                                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Metric</th>
                                {portfolioNames.map(name => (
                                  <th key={name} className="text-end" style={{ padding: '8px 12px', fontWeight: 600 }}>{displayName(name)}</th>
                                ))}
                                {hasCombined && (
                                  <th className="text-end" style={{ padding: '8px 12px', fontWeight: 700, borderLeft: '2px solid rgba(255,255,255,0.3)' }}>Combined</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              <tr><td colSpan={colCount} style={sectionStyle}>Return & Risk</td></tr>
                              {renderRow('Return/Risk Ratio', m => fmt2dp(m.return_risk_ratio), () => fmt2dp(combinedMetrics.return_risk_ratio))}
                              {renderRow('Sortino Ratio', m => fmt2dp(m.sortino_ratio), () => fmt2dp(combinedMetrics.sortino_ratio))}
                              {renderRow('Calmar Ratio', m => fmt2dp(m.calmar_ratio), () => fmt2dp(combinedMetrics.calmar_ratio))}

                              <tr><td colSpan={colCount} style={sectionStyle}>Drawdown</td></tr>
                              {renderRow('Avg Max Drawdown', m => fmtPct1(m.avg_max_drawdown), () => fmtPct1(combinedMetrics.avg_max_drawdown))}
                              {renderRow('Worst Max Drawdown', m => fmtPct1(m.worst_max_drawdown), () => fmtPct1(combinedMetrics.worst_max_drawdown))}
                              {renderRow('Worst 1-Year Return', m => fmtPct1(m.worst_1yr), () => fmtPct1(combinedMetrics.worst_1yr))}

                              <tr><td colSpan={colCount} style={sectionStyle}>Tail Risk</td></tr>
                              {renderRow('VaR (95%)', m => fmtPct1(m.var_95), () => fmtPct1(combinedMetrics.var_95))}
                              {renderRow('CVaR (95%)', m => fmtPct1(m.cvar_95), () => fmtPct1(combinedMetrics.cvar_95))}

                              <tr><td colSpan={colCount} style={sectionStyle}>Probability</td></tr>
                              {renderRow('Win Rate', m => fmtPct1(m.win_rate), () => fmtPct1(combinedMetrics.win_rate))}
                              {renderRow('Time Horizon', m => m.time_horizon != null ? m.time_horizon + 'yr+' : '—', () => '—')}
                              {renderRow('Objective', m => m.objective_label || fmtPct2(m.return_objective), () => '—')}
                              {renderRow('Prob. Meeting Objective',
                                m => m.objective_probability != null ? fmtPct1(m.objective_probability) + (m.time_horizon ? ` over ${m.time_horizon}yr` : '') : '—',
                                () => '—'
                              )}
                              {renderRow('Est. Negative Years (20yr)', m => fmt1dp(m.expected_negative_years), () => fmt1dp(combinedMetrics.expected_negative_years))}
                              {renderRow('SRM Risk Band', m => m.srm_risk_band != null ? m.srm_risk_band : '—', () => '—')}
                              {renderRow('SRM Risk Label', m => m.srm_risk_label ?? '—', () => '—')}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <h6><i className="bi bi-info-circle"></i> Portfolio Blender / Builder &ndash; Important Information</h6>
            <ManagedDisclaimer dkey="tool_blender" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>
      <BlenderMethodologyModal
        show={showMethodology}
        onClose={() => setShowMethodology(false)}
      />
    </div>
  );
};

export default PortfolioBlenderApp;
